// 🏭 외주 의뢰 (PC 케이스) — 2026-10-30 대표 요청, docs/계획_외주의뢰_페이지_2026-10-30.md §5
//   PC 드롭다운 → 그 PC 의 케이스(커스텀/일반) → CNC·메탈 프린트 + 연결된 제조사 → [보내기] → 보낸 외주 + 지그 사진·받기.
//   · STL 은 웹에 올리지 않는다: 에이전트가 케이스 폴더의 완성 STL 을 바로 outsourceMf/… 로 보낸다(명령 'outsource').
//   · 확인 창·납기 선택은 뺐다(대표 10-30). 대신 고르면 「보낼 내용」에 어버트먼트만 작은 3D 사진으로 바로 보인다(대표 10-30 "이렇게 미리보기가 나오면 어떨까, 어버트먼트만")
//     — 에이전트 'outsourcePreview' 가 줄인 GLB 를 agentPreview/… 에 올리고(24시간 뒤 서버가 지움) GlbThumb 가 사진으로 그린다. 누르면 StlViewer.
//   · 커스텀은 보내면 지그도 같이 만든다 — 외주 연결(mfOrderId) 지그 = 무료(한도 미차감), 지그 디자인값은 외주업체가 낸다(대표 10-30).
//     그래서 이 지그는 지그 메뉴에 안 나오고 여기서만 보고 받는다(Jig.js 가 channel cnc_outsource 를 뺀다).
//   데이터: agentStatus/{labId}/priv/cases_*·commands_*(JigPcCases 와 같은 문서) · outsourceOrders(flow mf, filesFrom agent) · jigJobs·jigResults(mfOrderId).
import React, { Suspense, lazy, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { collection, deleteField, doc, getDoc, getDocs, onSnapshot, query, setDoc, where } from 'firebase/firestore';
import { ref, getDownloadURL } from 'firebase/storage';
import { db, storage, auth } from '../firebase/config';
import { RefreshCw } from 'lucide-react';
import PageHeader from './common/PageHeader';
import { PhotoImg, jigPhotos, downloadJigResult } from './common/jigFiles';
import { useJigConsent, JigConsentGate } from './common/JigConsentGate';
import { relTime } from './JigPcCases';
import { createAgentMfOrder, listApprovedVendors, listMyLinksAsLab, listSentMf, newMfCaseNo, MF_STATUS, mfMsOf } from '../services/outsourceMfApi';
import { keyOf, nameOf, candidatesOf, cleanName, MAX_REMEMBERED } from '../utils/implantNameEdit';
import { DEFAULT_IMPLANTS, IMPLANT_CATALOG } from '../utils/implantCatalog';

const StlViewer = lazy(() => import('./StlViewer'));   // three.js 는 쓸 때만
const GlbThumb = lazy(() => import('./common/GlbThumb'));
const PREVIEW_WAIT_MS = 90 * 1000;   // PC 가 꺼져 있으면 미리보기가 안 온다 — 이만큼 기다린 뒤 "미리보기 없음"

const METHODS = [
    { kind: 'cnc', title: 'CNC', sub: '밀링 · 티타늄 · 지르코니아' },
    { kind: 'metal', title: 'Metal print', sub: 'SLM · 코발트크롬 · 티타늄' },
];
const KIND_WORD = { abutment: '어버트먼트', crown: '크라운', bridge: '브릿지', inlay: '인레이', onlay: '온레이', veneer: '비니어', coping: '코핑', bar: '바', other: '기타' };

function newCmdId() {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') return crypto.randomUUID();
    return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}
/** '1.0.0-alpha.115' → 115 (모르면 0) — 문자열 비교는 alpha.99 > alpha.116 으로 틀린다 */
const alphaOf = (v) => { const m = /alpha\.(\d+)/.exec(String(v || '')); return m ? Number(m[1]) : 0; };
/** 폴더 이름 "2026-10-30_00042-009-치과가는날 김OO3" → "치과가는날 김OO3" (외주 실명 칸) */
export function nameOfLabel(label) {
    return String(label || '').replace(/^\d{4}-\d{2}-\d{2}_\d+-\d+-/, '').trim().slice(0, 40);
}
/** 케이스 → 치식 숫자 배열(STL 이름에서) */
function teethOfCase(c) {
    const set = new Set();
    (c.designFiles || []).forEach((f) => (f.teeth || []).forEach((t) => set.add(Number(t))));
    if (!set.size) (c.teeth || []).forEach((t) => set.add(Number(t)));
    return [...set].filter((x) => Number.isInteger(x)).sort((a, b) => a - b);
}
/** 케이스의 임플란트 — 라이브러리별 하나씩(같은 라이브러리 여러 치아는 한 줄) */
function implantsUniq(c) {
    const seen = new Set();
    return (c.implants || []).filter((i) => { const k = keyOf(i) || `${i.system}|${i.connection}`; if (seen.has(k)) return false; seen.add(k); return true; });
}
function fileLine(c) {
    const fs = c.designFiles || [];
    if (!fs.length) return '';
    return fs.map((f) => `${(f.teeth || []).join('-') || '?'} ${KIND_WORD[f.kind] || ''}`.trim()).join(', ');
}
/** 의뢰서 상세 — CNC 는 임플란트 시스템 칸, 메탈은 종류·소재 칸 (rules mfDetailOk 키만) */
// system 40·connection 20자 = firestore.rules mfDetailOk 상한 — 옛 에이전트(alpha.120 이하)는 60자까지 보내 저장이 거절될 수 있었다(11-01)
//   nm(imp) = 화면에서 고친·기억한 이름까지 반영한 이름(11-01)
function detailOf(c, kind, nm = (i) => i.system || '') {
    const imp = (c.implants || [])[0] || {};
    if (kind === 'cnc' && c.custom) return { item: '커스텀 어버트먼트', material: imp.material === 'TI' ? '티타늄(Gr.5)' : '', system: (nm(imp) || '').slice(0, 40), connection: (imp.connection || '').slice(0, 20), platform: (imp.variant || '').slice(0, 20) };
    if (c.custom) return { item: '커스텀 어버트먼트', material: '', system: (nm(imp) || '').slice(0, 40), connection: (imp.connection || '').slice(0, 20), platform: (imp.variant || '').slice(0, 20) };
    const kinds = [...new Set((c.designFiles || []).map((f) => KIND_WORD[f.kind] || '기타'))];
    return { item: kinds.join('·').slice(0, 40), material: '' };
}

// embedded = 외주 의뢰서 「새 의뢰」 탭 안(대표 10-30 "외주의뢰서 PC 기준으로, 기존 외주의뢰는 없애고") — 머리말은 허브가 그린다
/** 의뢰 1건에 붙은 지그 문서들 중 보여 줄 하나 — 의뢰에 연결된 작업 번호가 있으면 그것, 없으면 가장 최근(11-02 다시 만들기 대응) */
export function latestJigOf(list, linkedJobId) {
    if (!Array.isArray(list) || !list.length) return undefined;
    const hit = linkedJobId && list.find((x) => x.jobId === linkedJobId);
    if (hit) return hit;
    const t = (x) => mfMsOf(x.doneAt) || mfMsOf(x.createdAt) || mfMsOf(x.updatedAt) || 0;
    return list.reduce((a, b) => (t(b) > t(a) ? b : a));
}

export default function OutsourceSend({ user, embedded = false, onFindVendors, onFileMode, delegationOk = null }) {
    const labId = user?.companyId || auth.currentUser?.uid;
    const consent = useJigConsent();
    const [docs, setDocs] = useState([]);
    const [cmdDocs, setCmdDocs] = useState({});
    const [loadErr, setLoadErr] = useState('');
    const [deviceNames, setDeviceNames] = useState({});
    const PICK_KEY = `outsrcPcPick_${labId || ''}`;
    const [picked, setPicked] = useState(() => { try { return localStorage.getItem(`outsrcPcPick_${labId || ''}`) || ''; } catch { return ''; } });
    const [tab, setTab] = useState('custom');
    const [filter, setFilter] = useState('');
    const [sel, setSel] = useState({});              // caseKey → true
    const [method, setMethod] = useState('cnc');
    const [vendors, setVendors] = useState([]);      // 연결 수락 + 승인된 업체 [{id,name,kinds}]
    const [vendorId, setVendorId] = useState('');
    const [note, setNote] = useState('');
    const [previewCmds, setPreviewCmds] = useState({});   // caseKey → { deviceId, cmdId, at, err? }
    const [viewer, setViewer] = useState(null);
    // 어버트먼트 사진 칸 높이 — 옆 케이스 목록의 아랫줄에 맞추고 넘치면 칸 안에서 스크롤(대표 10-30 "6개 이상되면 스크롤, 목록창 밑에줄에 맞춰서")
    const tableRef = useRef(null);
    const panelRef = useRef(null);
    const thumbsRef = useRef(null);
    const [thumbMax, setThumbMax] = useState(640);
    const [sending, setSending] = useState(false);
    const [msg, setMsg] = useState(null);            // { ok, text }
    const [sent, setSent] = useState([]);
    const [jigJobs, setJigJobs] = useState({});      // mfOrderId → jigJobs 문서
    const [jigResults, setJigResults] = useState({}); // mfOrderId → jigResults 문서
    const [remadeCount, setRemadeCount] = useState({}); // mfOrderId → 다시 만든 지그 수(지그·3D 모델 페이지에 있음, 11-02)
    const [busyDl, setBusyDl] = useState('');
    const [photoOf, setPhotoOf] = useState(null);    // 크게 보기 { list, idx, title }
    const [now, setNow] = useState(Date.now());
    // 임플란트 이름 고치기(11-01): edits = 이번 화면에서 고친 이름 { 'caseKey|열쇠': 이름 } · remembered = 기억한 이름 { 열쇠: {name, lib} } · editing = 고치는 중인 칸
    const [edits, setEdits] = useState({});
    const [remembered, setRemembered] = useState({});
    const [editing, setEditing] = useState(null);    // { caseKey, key, imp, draft, keep }
    const [savingName, setSavingName] = useState(false);
    const pick = (id) => { setPicked(id); setSel({}); try { localStorage.setItem(PICK_KEY, id); } catch { /* 저장 못 해도 이번엔 보인다 */ } };

    useEffect(() => { const t = setInterval(() => setNow(Date.now()), 15000); return () => clearInterval(t); }, []);

    // PC 목록·명령 — JigPcCases 와 같은 priv 구독
    useEffect(() => {
        if (!labId || !consent.agreed) return undefined;
        const unsub = onSnapshot(collection(db, 'agentStatus', labId, 'priv'), (snap) => {
            const cases = []; const cmds = {};
            snap.docs.forEach((d) => {
                if (d.id.startsWith('cases_')) { const x = d.data() || {}; cases.push({ deviceId: x.deviceId || d.id.slice(6), deviceName: x.deviceName || '', version: x.version || '', updatedAt: x.updatedAt, cases: x.cases || {} }); }
                else if (d.id.startsWith('commands_')) cmds[d.id.slice(9)] = (d.data() || {}).items || {};
                else if (d.id === 'implantNames') setRemembered((d.data() || {}).names || {});
            });
            cases.sort((a, b) => (Number(b.updatedAt) || 0) - (Number(a.updatedAt) || 0));
            setDocs(cases); setCmdDocs(cmds); setLoadErr('');
        }, (e) => { console.error('[외주 의뢰] PC 목록 구독 실패:', e?.code || e?.message); setLoadErr('PC 목록을 불러오지 못했습니다. 잠시 후 다시 시도해 주세요.'); });
        getDoc(doc(db, 'agentStatus', labId)).then((st) => setDeviceNames((st.exists() && st.data().deviceNames) || {})).catch((e) => console.error('[외주 의뢰] PC 이름표 실패:', e?.code || e?.message));
        return () => unsub();
    }, [labId, consent.agreed]);

    // 연결된 제조사 — 연결 수락(vendorLinks accepted, 그 종류 포함) + 업체 승인(vendorProfiles approved, 그 종류) — rules mfCreateOk 와 같은 조건
    useEffect(() => {
        if (!labId) return;
        Promise.all([listMyLinksAsLab(labId), listApprovedVendors()]).then(([links, profiles]) => {
            const byId = new Map(profiles.map((p) => [p.id, p]));
            const cncOk = (v) => (v.vendorType === 'cnc' || v.cncApproved === true) && !(v.gmpExpireAt && typeof v.gmpExpireAt.toMillis === 'function' && v.gmpExpireAt.toMillis() < Date.now());
            setVendors(links.filter((l) => l.status === 'accepted' && byId.has(l.vendorId)).map((l) => {
                const p = byId.get(l.vendorId);
                const kinds = (l.kinds || []).filter((k) => (p.kinds || []).includes(k) && (k !== 'cnc' || cncOk(p)));
                return { id: l.vendorId, name: p.businessName || l.vendorName || '', kinds };
            }).filter((v) => v.kinds.length));
        }).catch((e) => console.error('[외주 의뢰] 제조사 목록 실패:', e?.code || e?.message));
    }, [labId]);

    // 보낸 외주(이 페이지에서 PC 로 보낸 것만) + 연결된 지그
    const loadSent = useCallback(async () => {
        if (!labId) return;
        try {
            const list = (await listSentMf(labId)).filter((o) => o.filesFrom === 'agent');
            setSent(list);
            const [jj, jr] = await Promise.allSettled([
                getDocs(query(collection(db, 'jigJobs'), where('labId', '==', labId))),
                getDocs(query(collection(db, 'jigResults'), where('labId', '==', labId))),
            ]);
            const pickBy = (r) => { const m = {}; if (r.status === 'fulfilled') r.value.docs.forEach((d) => { const x = { jobId: d.id, ...d.data() }; if (x.mfOrderId) (m[x.mfOrderId] = m[x.mfOrderId] || []).push(x); }); else console.error('[외주 의뢰] 지그 목록 실패:', r.reason?.code || r.reason?.message); return m; };
            // 다시 만들기(remakeOf) 지그는 지그·3D 모델 페이지에서 본다(대표 11-02) — 여기는 외주로 처음 만든 지그만
            const remakeIds = new Set(jj.status === 'fulfilled' ? jj.value.docs.filter((d) => d.data().remakeOf).map((d) => d.id) : []);
            const rc = {};
            if (jj.status === 'fulfilled') jj.value.docs.forEach((d) => { const x = d.data(); if (x.remakeOf && x.mfOrderId) rc[x.mfOrderId] = (rc[x.mfOrderId] || 0) + 1; });
            setRemadeCount(rc);
            const dropRemakes = (m) => { Object.keys(m).forEach((k) => { m[k] = m[k].filter((x) => !remakeIds.has(x.jobId)); }); return m; };
            setJigJobs(dropRemakes(pickBy(jj))); setJigResults(dropRemakes(pickBy(jr)));
        } catch (e) { console.error('[외주 의뢰] 보낸 외주 목록 실패:', e?.code || e?.message); }
    }, [labId]);
    useEffect(() => { if (consent.agreed) loadSent(); }, [loadSent, consent.agreed]);

    const cur = docs.find((d) => d.deviceId === picked) || docs[0] || null;
    // 오늘 이 PC 로 보낸 케이스(명령 기록 24h) — 같은 케이스를 두 번 보내지 않게 표시
    const sentKeys = useMemo(() => {
        const m = {};
        Object.values((cur && cmdDocs[cur.deviceId]) || {}).forEach((c) => { if (c && c.type === 'outsource' && c.caseKey && c.status !== 'error') m[c.caseKey] = c; });
        return m;
    }, [cur, cmdDocs]);
    const rows = useMemo(() => {
        if (!cur) return [];
        const kw = filter.trim().toLowerCase();
        return Object.entries(cur.cases || {}).map(([caseKey, c]) => ({ caseKey, ...(c || {}) }))
            .filter((c) => (tab === 'custom' ? c.custom && !c.general : c.general))
            .filter((c) => !kw || String(c.label || '').toLowerCase().includes(kw) || teethOfCase(c).join(',').includes(kw))
            .sort((a, b) => (Number(b.maxMtimeMs) || 0) - (Number(a.maxMtimeMs) || 0));
    }, [cur, tab, filter]);
    const allCases = useMemo(() => (cur ? Object.entries(cur.cases || {}).map(([caseKey, c]) => ({ caseKey, ...(c || {}) })) : []), [cur]);
    const nCustom = allCases.filter((c) => c.custom && !c.general).length;
    const nGeneral = allCases.filter((c) => c.general).length;
    const selected = allCases.filter((c) => sel[c.caseKey]);
    const customSel = selected.filter((c) => c.custom && !c.general);
    const usableVendors = vendors.filter((v) => v.kinds.includes(method));
    useEffect(() => { if (!usableVendors.find((v) => v.id === vendorId)) setVendorId(usableVendors[0]?.id || ''); }, [method, vendors]); // eslint-disable-line react-hooks/exhaustive-deps

    const sendCmd = async (body) => {
        const cmdId = newCmdId();
        await setDoc(doc(db, 'agentStatus', labId, 'priv', 'commands_' + cur.deviceId), {
            items: { [cmdId]: { ...body, createdAt: Date.now(), by: auth.currentUser?.uid || '', status: 'pending' } },
        }, { merge: true });
        return cmdId;
    };

    // 어버트먼트 미리보기 — 커스텀 케이스를 고르면 PC 에 한 번 요청(이미 요청한 건 다시 안 함)
    const selKeys = customSel.map((c) => c.caseKey).join(',');
    useEffect(() => {
        if (!cur || !labId) return;
        const need = customSel.filter((c) => (c.designFiles || []).some((f) => f.kind === 'abutment') && (!previewCmds[c.caseKey] || previewCmds[c.caseKey].deviceId !== cur.deviceId));
        if (!need.length) return;
        (async () => {
            const add = {};
            for (const c of need) {
                try { add[c.caseKey] = { deviceId: cur.deviceId, cmdId: await sendCmd({ type: 'outsourcePreview', caseKey: c.caseKey }), at: Date.now() }; }
                catch (e) { console.error('[외주 의뢰] 미리보기 요청 실패:', e?.code || e?.message); add[c.caseKey] = { deviceId: cur.deviceId, cmdId: '', at: Date.now(), err: true }; }
            }
            setPreviewCmds((p) => ({ ...p, ...add }));
        })();
    }, [selKeys, cur?.deviceId]); // eslint-disable-line react-hooks/exhaustive-deps
    /** 케이스 → { state:'wait'|'done'|'none', previews:[어버트먼트만] } */
    const abutPreview = (c) => {
        const p = previewCmds[c.caseKey];
        if (!p) return { state: 'wait', previews: [] };
        if (p.err) return { state: 'none', previews: [] };
        const item = (cmdDocs[p.deviceId] || {})[p.cmdId];
        if (!item || item.status === 'pending' || item.status === 'running') return { state: now - p.at > PREVIEW_WAIT_MS ? 'none' : 'wait', previews: [] };
        if (item.status === 'error') return { state: 'none', previews: [] };
        return { state: 'done', previews: ((item.data && item.data.previews) || []).filter((x) => x.kind === 'abutment') };
    };
    const openViewer = async (c, previews) => {
        try {
            const entries = await Promise.all(previews.map(async (p) => ({
                url: await getDownloadURL(ref(storage, p.path)), path: p.path, name: `${(p.teeth || []).join('-')}_${p.kind}.glb`,
                tooth: (p.teeth || []).join('-'), kind: p.kind, format: 'glb-draco', size: p.bytes, reduced: !!p.reduced,
            })));
            setViewer({ entries, title: nameOfLabel(c.label) || '어버트먼트', notice: '줄인 미리보기입니다. 보낼 때는 PC의 원본 STL이 갑니다. 24시간 뒤 자동 삭제됩니다.' });
        } catch (e) {
            console.error('[외주 의뢰] 미리보기 열기 실패:', e?.code || e?.message);
            setMsg({ ok: false, text: '미리보기를 열지 못했습니다. 잠시 뒤 다시 눌러 주세요.' });
        }
    };
    // 「보낼 내용」 카드: 고른 커스텀 케이스의 어버트먼트 파일마다 1장(미리보기 오기 전엔 자리만)
    const abutCards = customSel.flatMap((c) => {
        const pv = abutPreview(c);
        const files = (c.designFiles || []).filter((f) => f.kind === 'abutment');
        return files.map((f) => ({ c, f, pv, preview: pv.previews.find((x) => x.n === f.n) || null }));
    });

    useEffect(() => {
        const fit = () => {
            const t = tableRef.current, p = panelRef.current, th = thumbsRef.current;
            if (!t || !p || !th) return;
            const side = Math.abs(p.getBoundingClientRect().top - t.getBoundingClientRect().top) < 8;   // 나란히 있을 때만(좁은 화면에서 아래로 내려가면 3줄 높이)
            const others = p.scrollHeight - th.offsetHeight;   // 사진 칸을 뺀 나머지 내용 높이
            const want = side ? t.offsetHeight - others : 640;
            setThumbMax((v) => { const n = Math.max(230, Math.round(want)); return Math.abs(n - v) > 2 ? n : v; });
        };
        fit();
        if (typeof ResizeObserver === 'undefined') return undefined;
        const ro = new ResizeObserver(fit);
        [tableRef.current, panelRef.current].forEach((el) => el && ro.observe(el));
        return () => ro.disconnect();
    });

    // 보낸 외주 → PC 가 남긴 결과 문구(지그를 왜 안 만들었는지 — 이미 기공소가 만든 케이스 등). 10-30 테스트: "지그 대기"로만 보여 헷갈림
    const sendResults = useMemo(() => {
        const m = {};
        Object.values(cmdDocs).forEach((items) => Object.values(items || {}).forEach((c) => { if (c && c.type === 'outsource' && c.orderId) m[c.orderId] = c; }));
        return m;
    }, [cmdDocs]);
    const states = selected.map((c) => ({ c, noStl: !(c.designFiles || []).length, already: !!sentKeys[c.caseKey] || !!c.mfOrderId }));
    const sendable = states.filter((s) => !s.noStl);   // 이미 외주로 보낸 건도 다시 보낼 수 있다 = 새 의뢰(대표 10-30 테스트 "선택란이 선택이 안 돼")
    const vendor = usableVendors.find((v) => v.id === vendorId) || null;

    // ── 임플란트 이름 고치기 · 기억하기 (대표 11-01) ──
    const nmOf = (c) => (imp) => nameOf(imp, c.caseKey, edits, remembered).name;
    const startEdit = (c, imp) => {
        const k = keyOf(imp);
        if (!k) return;
        setEditing({ caseKey: c.caseKey, key: k, imp, draft: nameOf(imp, c.caseKey, edits, remembered).name, keep: false });
    };
    const applyEdit = async () => {
        if (!editing || savingName) return;
        const name = cleanName(editing.draft);
        if (!name) { setMsg({ ok: false, text: '임플란트 이름을 적어 주세요.' }); return; }
        setEdits((e) => ({ ...e, [`${editing.caseKey}|${editing.key}`]: name }));
        if (editing.keep) {
            if (!remembered[editing.key] && Object.keys(remembered).length >= MAX_REMEMBERED) {
                setMsg({ ok: false, text: `기억해 둘 수 있는 이름이 가득 찼습니다(${MAX_REMEMBERED}개). 이번 케이스에만 적용했습니다.` });
                setEditing(null); return;
            }
            setSavingName(true);
            try {
                const lib = String(editing.imp.library || editing.imp.system || '').slice(0, 60);
                await setDoc(doc(db, 'agentStatus', labId, 'priv', 'implantNames'), {
                    names: { [editing.key]: { name, lib, by: auth.currentUser?.uid || '', at: Date.now() } }, updatedAt: Date.now(),
                }, { merge: true });
                setMsg({ ok: true, text: `「${lib}」은(는) 앞으로 「${name}」(으)로 나옵니다.` });
            } catch (e) {
                console.error('[외주 의뢰] 이름 기억 실패:', e?.code || e?.message);
                setMsg({ ok: false, text: '이름을 기억하지 못했습니다. 이번 케이스에는 고친 이름으로 보냅니다.' });
            } finally { setSavingName(false); }
        }
        setEditing(null);
    };
    const forgetName = async (key) => {
        if (savingName) return;
        setSavingName(true);
        try {
            await setDoc(doc(db, 'agentStatus', labId, 'priv', 'implantNames'), { names: { [key]: deleteField() }, updatedAt: Date.now() }, { merge: true });
            setMsg({ ok: true, text: '기억한 이름을 지웠습니다. PC가 읽은 이름으로 돌아갑니다.' });
        } catch (e) {
            console.error('[외주 의뢰] 이름 기억 지우기 실패:', e?.code || e?.message);
            setMsg({ ok: false, text: '기억한 이름을 지우지 못했습니다. 잠시 뒤 다시 해 주세요.' });
        } finally { setSavingName(false); setEditing(null); }
    };
    /** 커스텀 종류 칸 — 라이브러리마다 이름 · 연결 + [고치기] (고치는 중이면 후보·입력·기억 체크) */
    const implantCell = (c) => {
        const list = implantsUniq(c);
        if (!list.length) return <span style={S.dim}>{'의뢰서에 임플란트 정보 없음'}</span>;
        return list.map((imp) => {
            const k = keyOf(imp);
            const r = nameOf(imp, c.caseKey, edits, remembered);
            const isEd = editing && editing.caseKey === c.caseKey && editing.key === k;
            if (isEd) {
                const cands = candidatesOf(imp, remembered);
                return (
                    <div key={k} style={{ display: 'grid', gap: 6, padding: '6px 0' }}>
                        <div style={S.dim}>{`라이브러리: ${imp.library || imp.system}`}</div>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                            {cands.map((n) => <button key={n} type="button" onClick={() => setEditing((e) => ({ ...e, draft: n }))} style={{ ...S.ghost, padding: '2px 8px', fontSize: 12, ...(editing.draft === n ? { borderColor: '#4f46e5', color: '#4f46e5' } : {}) }}>{n}</button>)}
                        </div>
                        <select value="" onChange={(e) => { const v = e.target.value; if (v) setEditing((x) => ({ ...x, draft: v })); }} style={{ ...S.input, padding: '6px 8px' }} aria-label="전체 목록에서 고르기">
                            <option value="">{'전체 목록에서 고르기…'}</option>
                            <optgroup label="치과 의뢰서 기본">{DEFAULT_IMPLANTS.map((n) => <option key={n} value={n}>{n}</option>)}</optgroup>
                            {IMPLANT_CATALOG.map((g) => <optgroup key={g.brand} label={g.brand}>{g.names.map((n) => <option key={n} value={n}>{n}</option>)}</optgroup>)}
                        </select>
                        <input value={editing.draft} onChange={(e) => setEditing((x) => ({ ...x, draft: e.target.value.slice(0, 40) }))} placeholder="임플란트 회사·시스템 (예: DIO UF Regular)" style={{ ...S.input, padding: '6px 8px' }} aria-label="임플란트 이름" />
                        <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12.5 }}>
                            <input type="checkbox" checked={!!editing.keep} onChange={(e) => setEditing((x) => ({ ...x, keep: e.target.checked }))} />
                            {'이 라이브러리는 앞으로도 이 이름으로'}
                        </label>
                        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                            <button type="button" onClick={applyEdit} disabled={savingName} style={S.primarySm}>{savingName ? '저장 중…' : '적용'}</button>
                            <button type="button" onClick={() => setEditing(null)} style={S.ghost}>{'취소'}</button>
                            {remembered[k] && <button type="button" onClick={() => forgetName(k)} disabled={savingName} style={{ ...S.ghost, color: '#b91c1c' }}>{'기억한 이름 지우기'}</button>}
                        </div>
                    </div>
                );
            }
            return (
                <div key={k || r.name} style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap', padding: '2px 0' }}>
                    <span>{[r.name, imp.connection].filter(Boolean).join(' · ') || '—'}</span>
                    {r.from === 'edit' && <span style={{ ...S.pcTag, background: '#eef2ff', color: '#4338ca' }}>{'고침'}</span>}
                    {r.from === 'remembered' && <span style={{ ...S.pcTag, background: '#ecfdf5', color: '#047857' }}>{'기억한 이름'}</span>}
                    {k && <button type="button" onClick={() => startEdit(c, imp)} style={{ ...S.ghost, padding: '1px 8px', fontSize: 12 }} aria-label="임플란트 이름 고치기">{'고치기'}</button>}
                </div>
            );
        });
    };

    const doSend = async () => {
        if (sending || !sendable.length || !vendor) return;
        setSending(true); setMsg(null);
        const me = { companyId: labId, uid: auth.currentUser?.uid, name: user?.businessName || user?.companyName || user?.name || '' };
        let ok = 0; const fails = [];
        const skipped = states.length - sendable.length;   // 완성 STL 없음·이미 보낸 건
        for (const { c } of sendable) {
            const caseNo = newMfCaseNo();
            const fileNs = (c.designFiles || []).map((f) => f.n);
            const teeth = teethOfCase(c);
            try {
                const { id } = await createAgentMfOrder({
                    kind: method, vendor: { id: vendor.id, name: vendor.name }, patientName: nameOfLabel(c.label), memo: '',
                    teeth, bridge: (c.designFiles || []).some((f) => f.kind === 'bridge'), detail: detailOf(c, method, nmOf(c)), dueDate: '', note, fileCount: fileNs.length, caseNo,
                }, me);
                await sendCmd({ type: 'outsource', caseKey: c.caseKey, orderId: id, caseNo, fileNs, jig: !!(c.custom && !c.general) });
                ok += 1;
            } catch (e) {
                console.error('[외주 의뢰] 보내기 실패 case=%s:', c.caseKey.slice(0, 6), e?.code || e?.message);
                fails.push(e?.code === 'permission-denied' ? '권한 없음(외주 의뢰서 사용 동의·업체 연결을 확인)' : (e?.message || '실패'));
            }
        }
        setSending(false);
        setSel({});
        setMsg(fails.length ? { ok: ok > 0, text: `${ok}건 보냄 · ${fails.length}건 실패 — ${fails[0]}` } : { ok: true, text: `${ok}건 보냈습니다. PC가 STL을 올리면 업체에 알림이 갑니다.${customSel.length ? ' 커스텀은 지그도 같이 만듭니다(무료).' : ''}${skipped ? ` (${skipped}건은 완성 STL이 없거나 이미 보내서 뺐습니다)` : ''}` });
        loadSent();
    };

    const download = async (r) => {
        if (busyDl) return;
        setBusyDl(r.jobId);
        try { await downloadJigResult(r, labId); }
        catch (e) { console.error('[외주 의뢰] 지그 받기 실패:', e?.code || e?.message); setMsg({ ok: false, text: '지그를 받지 못했습니다. 보관 기간(7일)이 지났을 수 있습니다.' }); }
        finally { setBusyDl(''); }
    };

    const page = embedded ? { ...S.page, padding: 0, maxWidth: 'none' } : S.page;
    if (consent.loading) return <div style={page}>{!embedded && <PageHeader title="외주 의뢰" />}<div style={S.hint}>{'불러오는 중…'}</div></div>;
    if (!consent.agreed) return <div style={page}>{!embedded && <PageHeader title="외주 의뢰" />}<JigConsentGate consent={consent} /></div>;

    return (
        <div style={page}>
            {!embedded && <PageHeader title="외주 의뢰" subtitle="PC에서 찾은 케이스를 골라 CNC·메탈 프린트로 보냅니다. 완성 STL은 PC 폴더에서 자동으로 같이 가고, 커스텀은 지그도 무료로 같이 만들어집니다." />}
            {delegationOk === false && <div style={S.warn}>{'개정 약관(v7.1 — 다른 기공소에 맡기는 범위·환자 정보 처리) 동의 기록이 있어야 의뢰를 보낼 수 있습니다. 대표 계정으로 약관을 확인해 주세요.'}</div>}
            {msg && <div role="status" style={{ ...S.msg, ...(msg.ok ? S.msgOk : S.msgErr) }}>{msg.text}</div>}
            {loadErr && <div style={S.hint}>{loadErr}</div>}
            {!loadErr && !docs.length && <div style={S.hint}>{'아직 목록을 올린 PC가 없습니다. 덴클 프로그램(1.0.0-alpha.116 이상)이 켜져 있고 폴더가 연결되어 있으면 잠시 뒤 나타납니다.'}</div>}
            {onFileMode && <div style={{ ...S.hint, textAlign: 'right' }}><button type="button" onClick={onFileMode} style={{ background: 'none', border: 'none', color: '#4f46e5', cursor: 'pointer', fontSize: 12.5, textDecoration: 'underline' }}>{'PC 없이 STL 파일로 보내기 (지그 없음)'}</button></div>}
            {cur && (
                <>
                    <div style={S.toolbar}>
                        <label style={S.lbl} htmlFor="outsrc-pc">{'PC'}</label>
                        <select id="outsrc-pc" value={cur.deviceId} onChange={(e) => pick(e.target.value)} style={S.pcSelect}>
                            {docs.map((d) => <option key={d.deviceId} value={d.deviceId}>{`${deviceNames[d.deviceId] || d.deviceName || d.deviceId} — ${Object.keys(d.cases || {}).length}건 · ${relTime(d.updatedAt, now)}`}</option>)}
                        </select>
                        <input value={filter} onChange={(e) => setFilter(e.target.value)} placeholder="환자·치과·치식 검색" style={S.search} aria-label="검색" />
                        <span style={{ flex: 1 }} />
                        <div role="tablist" style={S.seg}>
                            <button type="button" role="tab" aria-selected={tab === 'custom'} onClick={() => setTab('custom')} style={tab === 'custom' ? S.segOn : S.segOff}>{`커스텀 ${nCustom}`}</button>
                            <button type="button" role="tab" aria-selected={tab === 'general'} onClick={() => setTab('general')} style={tab === 'general' ? S.segOn : S.segOff}>{`일반 ${nGeneral}`}</button>
                        </div>
                    </div>
                    {alphaOf(cur.version) > 0 && alphaOf(cur.version) < 116 && <div style={S.warn}>{`이 PC의 덴클 프로그램(${cur.version})은 외주 보내기를 아직 못 합니다. 1.0.0-alpha.116 이상으로 업데이트해 주세요.`}</div>}

                    <div style={S.cols}>
                        <div style={S.tableBox} ref={tableRef}>
                            <div style={S.boxHead}><b>{tab === 'custom' ? '커스텀 케이스' : '일반 케이스'}</b><span style={S.dim}>{`올린 시각 ${relTime(cur.updatedAt, now)}`}</span></div>
                            <div style={S.scrollBox}>
                                <table style={S.table}>
                                    <thead><tr style={S.thr}><th style={{ ...S.th, width: 36 }} /><th style={S.th}>{'케이스'}</th><th style={S.th}>{'치식'}</th><th style={S.th}>{tab === 'custom' ? '커스텀 종류' : '보철 종류'}</th><th style={S.th}>{'완성 STL'}</th><th style={S.th}>{'상태'}</th></tr></thead>
                                    <tbody>
                                        {rows.map((c) => {
                                            const noStl = !(c.designFiles || []).length;
                                            const already = sentKeys[c.caseKey] || c.mfOrderId;
                                            const dis = noStl;
                                            return (
                                                <tr key={c.caseKey} style={sel[c.caseKey] ? S.trOn : S.tr}>
                                                    <td style={S.td}><input type="checkbox" checked={!!sel[c.caseKey]} disabled={dis} onChange={() => setSel((s) => ({ ...s, [c.caseKey]: !s[c.caseKey] }))} style={{ width: 18, height: 18 }} aria-label="고르기" /></td>
                                                    <td style={{ ...S.td, minWidth: 220 }}><div style={{ fontWeight: 600 }}>{c.label}</div><div style={S.dim}>{c.kind === '3shape' ? '3Shape' : 'exocad'}</div></td>
                                                    <td style={{ ...S.td, fontWeight: 600, whiteSpace: 'nowrap' }}>{teethOfCase(c).join(', ') || '—'}</td>
                                                    <td style={{ ...S.td, minWidth: 180 }}>{tab === 'custom' ? implantCell(c) : ([...new Set((c.designFiles || []).map((f) => KIND_WORD[f.kind] || '기타'))].join(' · ') || '—')}</td>
                                                    <td style={{ ...S.td, minWidth: 140 }}>{noStl ? <span style={S.amber}>{'STL 없음 — 디자인 저장 전'}</span> : <span style={S.dim}>{`${c.designFiles.length}개 · ${fileLine(c)}`}</span>}</td>
                                                    <td style={{ ...S.td, whiteSpace: 'nowrap' }}>{noStl ? <span style={S.badgeOff}>{'못 보냄'}</span> : already ? <span style={S.badgeOk}>{'외주 보냄'}</span> : <span style={S.badge}>{'보낼 수 있음'}</span>}</td>
                                                </tr>
                                            );
                                        })}
                                        {!rows.length && <tr><td colSpan={6} style={{ ...S.td, ...S.dim, textAlign: 'center', padding: 24 }}>{'이 PC에 해당 케이스가 없습니다.'}</td></tr>}
                                    </tbody>
                                </table>
                            </div>
                            <div style={S.foot}>{'커스텀 종류는 의뢰서(constructionInfo)의 임플란트 라이브러리를 PC가 읽어 올립니다. 이름이 다르면 [고치기]로 바꿔 보낼 수 있습니다. 「외주 보냄」 건도 다시 고르면 새 의뢰로 한 번 더 갑니다. 완성 STL이 없는 건은 고를 수 없습니다.'}</div>
                        </div>

                        <div style={S.panel} ref={panelRef}>
                            <div style={S.boxHead}><b>{'보낼 내용'}</b><span style={S.dim}>{selected.length ? `케이스 ${selected.length}건` : '케이스를 고르세요'}</span></div>
                            <div style={S.lblTop}>{'제작 종류'}</div>
                            <div style={S.methods}>
                                {METHODS.map((m) => (
                                    <button key={m.kind} type="button" aria-pressed={method === m.kind} onClick={() => setMethod(m.kind)} style={method === m.kind ? S.mOn : S.mOff}>
                                        <div style={{ fontWeight: 700 }}>{m.title}</div><div style={{ fontSize: 11.5, opacity: 0.85 }}>{m.sub}</div>
                                    </button>
                                ))}
                            </div>
                            <label style={S.lblTop} htmlFor="outsrc-vendor">{'받는 곳 (연결된 제조사)'}</label>
                            {usableVendors.length
                                ? <select id="outsrc-vendor" value={vendorId} onChange={(e) => setVendorId(e.target.value)} style={S.input}>{usableVendors.map((v) => <option key={v.id} value={v.id}>{v.name || v.id}</option>)}</select>
                                : <div style={S.warn}>{`${method === 'cnc' ? 'CNC' : '메탈 프린트'}를 받는 연결된 제조사가 없습니다. 위 [외주 업체 찾기]에서 연결을 먼저 요청해 주세요.`}{onFindVendors && <div style={{ marginTop: 8 }}><button type="button" onClick={onFindVendors} style={S.primarySm}>{'외주 업체 찾기'}</button></div>}</div>}
                            <label style={S.lblTop} htmlFor="outsrc-note">{'요청 사항'}</label>
                            <textarea id="outsrc-note" rows={2} value={note} onChange={(e) => setNote(e.target.value.slice(0, 500))} placeholder="예: 47번 컨택 강하게 (환자 이름은 적지 마세요)" style={{ ...S.input, resize: 'vertical' }} />
                            {abutCards.length > 0 && (
                                <>
                                    <div style={{ ...S.lblTop, display: 'flex', justifyContent: 'space-between', gap: 8, flexWrap: 'wrap' }}><span>{'같이 보낼 어버트먼트'}</span><span style={{ ...S.dim, fontWeight: 400 }}>{'PC 케이스 폴더에서 자동으로 찾음'}</span></div>
                                    <div style={{ ...S.thumbs, maxHeight: thumbMax }} ref={thumbsRef}>
                                        {abutCards.map(({ c, f, pv, preview }) => (
                                            <div key={`${c.caseKey}_${f.n}`} style={S.thumbCard}>
                                                {preview
                                                    ? <Suspense fallback={<div style={S.thumbEmpty}>{'그리는 중…'}</div>}><GlbThumb path={preview.path} alt={`${(f.teeth || []).join('-')}번 어버트먼트`} onClick={() => openViewer(c, pv.previews)} /></Suspense>
                                                    : <div style={S.thumbEmpty}>{pv.state === 'wait' ? 'PC에서 불러오는 중…' : '미리보기 없음'}</div>}
                                                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}><b style={{ fontSize: 15 }}>{(f.teeth || []).join('-') || '—'}</b><span style={S.pcTag}>{'PC 폴더'}</span></div>
                                                <div style={{ ...S.dim, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{nameOfLabel(c.label) || c.label}</div>
                                            </div>
                                        ))}
                                    </div>
                                </>
                            )}
                            {/* 10-30 대표 "안내문 안나오게" — PC 폴더 자동 안내·다시 보내기 경고·커스텀 지그 안내 상자 뺌 */}
                            <button type="button" disabled={!sendable.length || !vendor || sending} onClick={doSend} style={sendable.length && vendor && !sending ? S.primary : S.primaryOff}>
                                {sending ? '보내는 중…' : sendable.length ? `보내기 · ${sendable.length}건` : selected.length ? '고른 케이스에 보낼 STL이 없습니다' : '케이스를 고르면 보낼 수 있습니다'}
                            </button>
                        </div>
                    </div>
                </>
            )}

            <div style={S.tableBox}>
                <div style={S.boxHead}><b>{'보낸 외주'}</b><button type="button" onClick={loadSent} style={S.ghost}><RefreshCw size={14} /> {'새로고침'}</button></div>
                <div style={S.scrollBox}>
                    <table style={{ ...S.table, minWidth: 820 }}>
                        <thead><tr style={S.thr}><th style={S.th}>{'보낸 날'}</th><th style={S.th}>{'케이스 번호'}</th><th style={S.th}>{'치식'}</th><th style={S.th}>{'제작 · 받는 곳'}</th><th style={S.th}>{'외주 상태'}</th><th style={S.th}>{'지그 (업체 제작 · 무료)'}</th></tr></thead>
                        <tbody>
                            {sent.map((o) => {
                                const st = MF_STATUS[o.status] || MF_STATUS.pending;
                                // 한 의뢰에 지그가 여럿(다시 만들기, 11-02) — 의뢰에 연결된 작업(o.jigJobId) 먼저, 없으면 가장 최근 것
                                // 다시 만들기는 위에서 뺐다 → 연결 작업(o.jigJobId)이 다시 만들기면 목록에 없어 가장 최근 = 처음 외주 지그
                                const jr = latestJigOf(jigResults[o.id], o.jigJobId);
                                const jj = latestJigOf(jigJobs[o.id], o.jigJobId);
                                const remade = remadeCount[o.id] || 0;
                                const photos = jr ? jigPhotos(jr) : [];
                                const d = new Date(mfMsOf(o.createdAt) || 0);
                                const p2 = (n) => String(n).padStart(2, '0');
                                return (
                                    <tr key={o.id} style={S.tr}>
                                        <td style={{ ...S.td, whiteSpace: 'nowrap' }}>{mfMsOf(o.createdAt) ? `${p2(d.getMonth() + 1)}-${p2(d.getDate())} ${p2(d.getHours())}:${p2(d.getMinutes())}` : '—'}</td>
                                        <td style={{ ...S.td, fontWeight: 600 }}>{o.caseNo}</td>
                                        <td style={{ ...S.td, whiteSpace: 'nowrap' }}>{String(o.teeth || '').replace(/,/g, ', ') || '—'}</td>
                                        <td style={S.td}>{`${o.kind === 'metal' ? 'Metal print' : 'CNC'} · ${o.toName || ''}`}</td>
                                        <td style={S.td}>
                                            <span style={{ ...S.pill, color: st.fg, background: st.bg }}>{st.text}</span>
                                            {!o.filesReadyAt && !['rejected', 'cancelled'].includes(o.status) && <div style={{ ...S.dim, marginTop: 4 }}>{'PC에서 STL 올리는 중'}</div>}
                                        </td>
                                        <td style={S.td}>
                                            {jr ? (
                                                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                                                    {photos.length > 0 && (
                                                        <button type="button" onClick={() => setPhotoOf({ list: photos, idx: 0, title: o.caseNo })} style={S.photoBtn} title="지그 사진 크게 보기">
                                                            <PhotoImg path={photos[0].path} alt="지그 사진" style={{ width: 112, height: 76, objectFit: 'cover', borderRadius: 6, display: 'block' }} />
                                                        </button>
                                                    )}
                                                    <div>
                                                        <button type="button" onClick={() => download(jr)} disabled={!!busyDl} style={S.primarySm}>{busyDl === jr.jobId ? '받는 중…' : '지그 받기 · 무료'}</button>
                                                        <div style={{ ...S.dim, marginTop: 4 }}>{`제작: ${o.toName || '외주업체'}`}</div>
                                                        {remade > 0 && <div style={S.dim}>{`다시 만든 지그 ${remade}개 — 지그·3D 모델 메뉴에서 받기`}</div>}
                                                        {mfMsOf(jr.createdAt) > 0 && (() => { const m = new Date(mfMsOf(jr.createdAt)); return <div style={S.dim}>{`지그 만든 시각 ${p2(m.getMonth() + 1)}-${p2(m.getDate())} ${p2(m.getHours())}:${p2(m.getMinutes())}`}</div>; })()}
                                                    </div>
                                                </div>
                                            ) : jj ? (
                                                <span style={jj.status === 'error' ? S.amber : S.dim}>{jj.status === 'error' ? `지그 실패 — ${jj.error || '사유 없음'}` : '지그 만드는 중… 끝나면 사진이 뜹니다'}</span>
                                            ) : (
                                                (() => {
                                                    if (o.detail?.item !== '커스텀 어버트먼트') return <span style={S.dim}>{'일반 보철 — 지그 없음'}</span>;
                                                    const r = sendResults[o.id];
                                                    const t = String((r && (r.result || r.error)) || '');
                                                    if (r && r.status === 'error') return <span style={S.amber}>{`PC 전송 실패 — ${t || '사유 없음'}`}</span>;
                                                    if (t.includes('이미 보낸')) return <span style={S.dim}>{'기공소에서 이미 만든 지그라 외주 지그를 만들지 않았습니다(옛 PC 프로그램)'}</span>;
                                                    if (t.includes('만드는 중')) return <span style={S.dim}>{'기공소 지그를 만드는 중이라 외주 지그는 만들지 않았습니다'}</span>;
                                                    if (/지그 (연결 실패|못 보냄)/.test(t)) return <span style={S.amber}>{t.split('·').pop().trim()}</span>;
                                                    return <span style={S.dim}>{'지그 대기 (PC가 보내면 시작)'}</span>;
                                                })()
                                            )}
                                        </td>
                                    </tr>
                                );
                            })}
                            {!sent.length && <tr><td colSpan={6} style={{ ...S.td, ...S.dim, textAlign: 'center', padding: 24 }}>{'아직 이 페이지에서 보낸 외주가 없습니다.'}</td></tr>}
                        </tbody>
                    </table>
                </div>
            </div>

            {viewer && (
                <Suspense fallback={null}>
                    <StlViewer entries={viewer.entries} title={viewer.title} notice={viewer.notice} onClose={() => setViewer(null)} />
                </Suspense>
            )}
            {photoOf && (
                <div role="dialog" aria-modal="true" aria-label="지그 사진" style={S.overlay} onClick={() => setPhotoOf(null)}>
                    <div style={{ ...S.modal, maxWidth: 720 }} onClick={(e) => e.stopPropagation()}>
                        <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                            <b style={{ flex: 1 }}>{`지그 사진 · ${photoOf.title}`}</b>
                            {photoOf.list.map((p, i) => <button key={p.path} type="button" onClick={() => setPhotoOf({ ...photoOf, idx: i })} style={i === photoOf.idx ? S.segOn : S.segOff}>{p.label}</button>)}
                            <button type="button" onClick={() => setPhotoOf(null)} style={S.ghost} aria-label="닫기">{'×'}</button>
                        </div>
                        <PhotoImg path={photoOf.list[photoOf.idx].path} alt={photoOf.list[photoOf.idx].label} style={{ width: '100%', borderRadius: 8, display: 'block' }} />
                    </div>
                </div>
            )}
        </div>
    );
}

const S = {
    page: { padding: '24px 20px 48px', maxWidth: 1280, margin: '0 auto', display: 'flex', flexDirection: 'column', gap: 16 },
    hint: { padding: 16, background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 10, color: '#475569', fontSize: 13.5 },
    msg: { padding: '10px 14px', borderRadius: 10, fontSize: 13.5 },
    msgOk: { background: '#ecfdf5', color: '#065f46' },
    msgErr: { background: '#fef2f2', color: '#991b1b' },
    toolbar: { display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'center', background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, padding: '12px 14px' },
    lbl: { fontSize: 12.5, fontWeight: 600, color: '#334155' },
    lblTop: { fontSize: 12.5, fontWeight: 600, color: '#334155', marginTop: 4 },
    pcSelect: { flex: '0 1 380px', minWidth: 0, fontSize: 13.5, fontWeight: 600, padding: '9px 10px', border: '1px solid #94a3b8', borderRadius: 8, background: '#fff', minHeight: 40 },
    search: { flex: '0 1 240px', minWidth: 0, fontSize: 13.5, padding: '9px 10px', border: '1px solid #cbd5e1', borderRadius: 8, minHeight: 40, boxSizing: 'border-box' },
    seg: { display: 'inline-flex', padding: 3, background: '#f1f5f9', borderRadius: 10, gap: 2 },
    segOn: { border: 0, borderRadius: 8, padding: '0 14px', minHeight: 34, fontSize: 13, fontWeight: 700, background: '#fff', color: '#312e81', boxShadow: '0 1px 2px rgba(15,23,42,0.12)', cursor: 'pointer' },
    segOff: { border: 0, borderRadius: 8, padding: '0 14px', minHeight: 34, fontSize: 13, fontWeight: 600, background: 'transparent', color: '#475569', cursor: 'pointer' },
    cols: { display: 'flex', flexWrap: 'wrap', gap: 16, alignItems: 'flex-start' },
    tableBox: { flex: '999 1 640px', minWidth: 0, background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, overflow: 'hidden' },
    boxHead: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, padding: '12px 16px', borderBottom: '1px solid #e2e8f0', flexWrap: 'wrap' },
    table: { width: '100%', borderCollapse: 'collapse', fontSize: 13, minWidth: 720 },
    thr: { background: '#f8fafc', color: '#334155', textAlign: 'left' },
    th: { padding: '10px 10px', fontWeight: 600, position: 'sticky', top: 0, background: '#f8fafc', zIndex: 1 },
    scrollBox: { overflow: 'auto', maxHeight: 640 },   // 12줄 안팎이 넘으면 표 안에서 아래로 내린다(대표 10-30 "10~15줄 정도되면 스크롤"), 머리줄은 고정
    tr: { borderTop: '1px solid #eef2f6' },
    trOn: { borderTop: '1px solid #eef2f6', background: '#f5f7ff' },
    td: { padding: '10px 10px', verticalAlign: 'top' },
    dim: { fontSize: 12, color: '#64748b' },
    amber: { fontSize: 12, color: '#92400e' },
    badge: { display: 'inline-block', padding: '3px 9px', borderRadius: 999, fontSize: 11.5, fontWeight: 600, background: '#eef2ff', color: '#3730a3' },
    badgeOk: { display: 'inline-block', padding: '3px 9px', borderRadius: 999, fontSize: 11.5, fontWeight: 600, background: '#ecfdf5', color: '#065f46' },
    pill: { display: 'inline-block', padding: '3px 9px', borderRadius: 999, fontSize: 11.5, fontWeight: 600 },
    foot: { padding: '10px 16px', fontSize: 11.5, color: '#64748b', borderTop: '1px solid #e2e8f0' },
    panel: { flex: '1 1 320px', minWidth: 0, background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, padding: '0 16px 16px', display: 'flex', flexDirection: 'column', gap: 8 },
    methods: { display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 8 },
    mOn: { textAlign: 'left', padding: 12, minHeight: 60, borderRadius: 10, border: '2px solid #4338ca', background: '#eef2ff', color: '#312e81', cursor: 'pointer' },
    mOff: { textAlign: 'left', padding: 12, minHeight: 60, borderRadius: 10, border: '1px solid #cbd5e1', background: '#fff', color: '#334155', cursor: 'pointer' },
    input: { fontSize: 13.5, padding: '9px 10px', border: '1px solid #cbd5e1', borderRadius: 8, background: '#fff', minHeight: 40, boxSizing: 'border-box', width: '100%' },
    info: { fontSize: 12, color: '#334155', background: '#f8fafc', border: '1px dashed #cbd5e1', borderRadius: 8, padding: '9px 10px', lineHeight: 1.5 },
    thumbs: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(130px, 1fr))', gap: 10, overflowY: 'auto', alignContent: 'start', paddingRight: 2 },
    thumbCard: { border: '1px solid #e2e8f0', borderRadius: 12, padding: 10, display: 'flex', flexDirection: 'column', gap: 6, minWidth: 0 },
    thumbEmpty: { width: '100%', aspectRatio: '1 / 1', borderRadius: 8, background: '#eef1f5', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11.5, color: '#64748b', textAlign: 'center', padding: 6, boxSizing: 'border-box' },
    pcTag: { fontSize: 11, fontWeight: 700, color: '#166534', background: '#dcfce7', borderRadius: 999, padding: '2px 8px' },
    badgeOff: { fontSize: 12, fontWeight: 700, color: '#92400e', background: '#fef3c7', borderRadius: 999, padding: '3px 10px' },
    okBox: { fontSize: 12, color: '#065f46', background: '#ecfdf5', borderRadius: 8, padding: '9px 10px', lineHeight: 1.5 },
    warn: { fontSize: 12.5, color: '#92400e', background: '#fffbeb', borderRadius: 8, padding: '9px 10px' },
    primary: { border: 0, borderRadius: 10, minHeight: 46, padding: '0 18px', fontSize: 14.5, fontWeight: 700, background: '#4338ca', color: '#fff', cursor: 'pointer' },
    primaryOff: { border: 0, borderRadius: 10, minHeight: 46, padding: '0 18px', fontSize: 14, fontWeight: 600, background: '#e2e8f0', color: '#475569', cursor: 'not-allowed' },
    primarySm: { border: 0, borderRadius: 8, minHeight: 36, padding: '0 12px', fontSize: 12.5, fontWeight: 600, background: '#4338ca', color: '#fff', cursor: 'pointer', whiteSpace: 'nowrap' },
    ghost: { display: 'inline-flex', alignItems: 'center', gap: 4, border: '1px solid #cbd5e1', background: '#fff', color: '#334155', borderRadius: 8, minHeight: 34, padding: '0 12px', fontSize: 12.5, cursor: 'pointer' },
    photoBtn: { border: '1px solid #e2e8f0', background: '#f1f5f9', borderRadius: 8, padding: 0, cursor: 'zoom-in' },
    overlay: { position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.45)', display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: '40px 16px', overflowY: 'auto', zIndex: 1000 },
    modal: { background: '#fff', borderRadius: 14, width: '100%', maxWidth: 780, padding: 20, display: 'flex', flexDirection: 'column', gap: 12, boxShadow: '0 20px 50px rgba(15,23,42,0.25)' },
    cards: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 10 },
    card: { border: '1px solid #e2e8f0', borderRadius: 12, padding: 12, display: 'flex', flexDirection: 'column', gap: 6 },
};
